import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTrackRecord } from "@/hooks/useTrackRecord";
import { computeScores } from "@/lib/trackRecord";
import { TRACK_BG, TRACK_ICON, TRACK_TEXT, TRACK_TINT } from "./trackColors";
import { trackTiles } from "./trackTiles";

/**
 * Three-across track record for tight spaces (the account menu): the count
 * that matters per track, with the track's score as a thin bar. Same source
 * and colours as the profile tiles, so the two never disagree.
 *
 * Renders nothing on a failed or missing read — a menu showing zeros would
 * read as "you've done nothing", which is worse than no strip.
 */
export function TrackRecordMini({ profileId }: { profileId: string | null | undefined }) {
  const { t } = useTranslation();
  const { data: record, isPending } = useTrackRecord(profileId);
  const scores = useMemo(() => (record ? computeScores(record) : null), [record]);

  if (!profileId) return null;

  if (isPending) {
    return (
      <div className="grid grid-cols-3 gap-1.5" aria-hidden data-testid="track-mini-loading">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-[70px] motion-safe:animate-pulse rounded-lg bg-surface-tertiary" />
        ))}
      </div>
    );
  }

  if (!record || !scores) return null;

  const tiles = trackTiles(record, scores).map((tile) => ({
    key: tile.key,
    icon: TRACK_ICON[tile.key],
    count: tile.count,
    title: t(tile.detail.key, tile.detail.params),
  }));

  return (
    <ul className="grid grid-cols-3 gap-1.5" aria-label={t("trackRecord.title")} data-testid="track-mini">
      {tiles.map(({ key, icon: Icon, count, title }) => (
        <li
          key={key}
          title={title}
          className={`min-w-0 rounded-lg px-2 py-2 ${TRACK_TINT[key]}`}
          data-testid={`track-mini-${key}`}
        >
          <div className="flex items-center gap-1" aria-hidden>
            <Icon className={`h-4 w-4 shrink-0 ${TRACK_TEXT[key]}`} />
            <span className="text-lg font-bold leading-none tabular-nums text-text-primary">{count}</span>
          </div>
          {/* Sentence case at 12px: uppercase at a readable size truncated
              "Voluntário" in a third of the menu, and all-caps reads slower. */}
          <div className={`mt-1 truncate text-xs font-semibold ${TRACK_TEXT[key]}`} aria-hidden>
            {t(`topbar.stats.${key}`)}
          </div>
          {/* The visible tile is a count and a (possibly truncated) label; a
              screen reader gets the whole sentence instead. */}
          <span className="sr-only">{`${t(`topbar.stats.${key}`)}: ${title}`}</span>
          <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-surface-tertiary" aria-hidden>
            <div className={`h-full rounded-full ${TRACK_BG[key]}`} style={{ width: `${scores[key]}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}
